// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UserCircle2, Award, Images } from 'lucide-react';

interface UserProfilePreviewDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  userId: string | null;
}

export default function UserProfilePreviewDialog({ open, onOpenChange, userId }: UserProfilePreviewDialogProps) {
  const [profile, setProfile] = useState<any | null>(null);
  const [collections, setCollections] = useState<any[]>([]);
  const [awards, setAwards] = useState<any[]>([]);

  useEffect(() => {
    if (!open || !userId) return;
    (async () => {
      try {
        const { data: p } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email, avatar_url, bio, website, last_seen')
          .eq('id', userId)
          .maybeSingle();
        setProfile(p || null);
      } catch {}
      try {
        const { data: cols } = await supabase
          .from('user_collections')
          .select('id, title, visibility')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(6);
        setCollections(cols || []);
      } catch {}
      try {
        const { data: a } = await supabase
          .from('profile_showcase_awards')
          .select('*, user_awards(*, awards_catalog(*))')
          .eq('user_id', userId)
          .limit(3);
        setAwards(a || []);
      } catch {}
    })();
  }, [open, userId]);

  const name = profile ? [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'User' : 'User';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-slate-950/95 border border-white/10 text-white"
        onEscapeKeyDown={(e) => {
          // Close only the profile dialog; keep parent chat overlay open
          e.preventDefault();
          e.stopPropagation();
          onOpenChange(false);
        }}
      >
        <div className="sr-only" aria-hidden="true">
          <h2>Profile</h2>
          <p>Preview of user profile details</p>
        </div>
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 rounded-full overflow-hidden border border-white/20 bg-white/10 flex items-center justify-center">
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="avatar" className="h-full w-full object-cover" />
            ) : (
              <UserCircle2 className="h-8 w-8 text-white/70" />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-lg font-medium text-white truncate">{name}</div>
            <div className="text-xs text-white/60 truncate">{profile?.email}</div>
            <div className="text-xs text-white/50 mt-1">{profile?.bio}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white flex items-center gap-2"><Images className="h-4 w-4" /> Collections</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {collections.length === 0 && <div className="text-xs text-white/60">No collections</div>}
              {collections.map((c) => (
                <div key={c.id} className="flex items-center justify-between text-sm">
                  <div className="truncate text-white/90">{c.title || 'Untitled'}</div>
                  <Badge className="bg-white/10 border-white/20 text-white/70">{c.visibility}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white flex items-center gap-2"><Award className="h-4 w-4" /> Showcase Awards</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {awards.length === 0 && <div className="text-xs text-white/60">No showcase awards</div>}
              {awards.map((a) => (
                <div key={a.id} className="text-sm text-white/90 truncate">{a.user_awards?.awards_catalog?.name || 'Award'}</div>
              ))}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}


